import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Outlet } from "react-router";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { UserRegionProvider } from "@/contexts/UserRegionContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import React, { Suspense } from "react";
import { Layout } from "@/components/layout/Layout";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { BrandLoader } from "@/components/ui/BrandLoader";
// Critical content paths stay eager (likely entry points / LCP).
import Index from "./pages/Index.tsx";
import CategoryPage from "./pages/CategoryPage.tsx";
import ArticlePage from "./pages/ArticlePage.tsx";
import { PageTracker } from "@/components/analytics/PageTracker";
import { GoogleConsentBanner } from "@/components/analytics/GoogleConsentBanner";
import { DirtyStateProvider } from "@/contexts/DirtyStateContext";

// Secondary / legal / auth routes are code-split so they don't bloat the
// initial bundle or delay first paint of the content pages above.
const NotFound = React.lazy(() => import("./pages/NotFound.tsx"));
const AboutPage = React.lazy(() => import("./pages/AboutPage.tsx"));
const ContactPage = React.lazy(() => import("./pages/ContactPage.tsx"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage.tsx"));
const DisclaimerPage = React.lazy(() => import("./pages/DisclaimerPage.tsx"));
const TermsPage = React.lazy(() => import("./pages/TermsPage.tsx"));
const SignInPage = React.lazy(() => import("./pages/SignInPage.tsx"));
const ForgotPasswordPage = React.lazy(
  () => import("./pages/ForgotPasswordPage.tsx"),
);
const ResetPasswordPage = React.lazy(
  () => import("./pages/ResetPasswordPage.tsx"),
);
const BookmarksPage = React.lazy(() => import("./pages/BookmarksPage.tsx"));
const TagPage = React.lazy(() => import("./pages/TagPage.tsx"));
const SearchResultsPage = React.lazy(() => import("./pages/SearchPage.tsx"));
const CategoriesPage = React.lazy(() => import("./pages/CategoriesPage.tsx"));
const RecentArticlesPage = React.lazy(
  () => import("./pages/RecentArticlesPage.tsx"),
);
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard.tsx"));
const CategoryEditPage = React.lazy(() => import("./pages/CategoryEditPage.tsx"));
const ArticleEditPage = React.lazy(() => import("./pages/ArticleEditPage.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
    },
  },
});

/** Public Layout Shell: Header & Footer remain permanently mounted across public navigation. */
const PublicRouteShell = () => (
  <Layout>
    <Suspense fallback={<BrandLoader variant="content" />}>
      <Outlet />
    </Suspense>
  </Layout>
);

/** Auth Layout Shell: Minimalist navbar with only logo and clean copyright bar (no footer). */
const AuthRouteShell = () => (
  <AuthLayout>
    <Suspense fallback={<BrandLoader variant="content" />}>
      <Outlet />
    </Suspense>
  </AuthLayout>
);

/** Fullscreen Brand Transition for Admin & Cold Boot boundaries. */
const AdminSuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<BrandLoader variant="fullscreen" />}>
    {children}
  </Suspense>
);

const AppRoutes = () => {
  return (
    <Routes>
      {/* ── Public persistent layout shell ── */}
      <Route element={<PublicRouteShell />}>
        <Route path="/" element={<Index />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/article/:slug" element={<ArticlePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/bookmarks" element={<BookmarksPage />} />
        <Route path="/tag/:slug" element={<TagPage />} />
        <Route path="/search" element={<SearchResultsPage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/recent" element={<RecentArticlesPage />} />
        <Route path="/recent-articles" element={<RecentArticlesPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* ── Auth layout shell (Logo-only navbar, minimal copyright footer) ── */}
      <Route element={<AuthRouteShell />}>
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* ── Admin routes (Dedicated AdminLayout with Fullscreen brand loader) ── */}
      <Route
        path="/admin"
        element={
          <AdminSuspenseWrapper>
            <AdminDashboard />
          </AdminSuspenseWrapper>
        }
      />
      <Route
        path="/admin/categories/:id"
        element={
          <AdminSuspenseWrapper>
            <CategoryEditPage />
          </AdminSuspenseWrapper>
        }
      />
      <Route
        path="/admin/articles/:id"
        element={
          <AdminSuspenseWrapper>
            <ArticleEditPage />
          </AdminSuspenseWrapper>
        }
      />
    </Routes>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <TooltipProvider>
            <Toaster />
            <BrowserRouter>
              <LanguageProvider>
                <UserRegionProvider>
                  <DirtyStateProvider>
                    <PageTracker />
                    <GoogleConsentBanner />
                    <ErrorBoundary>
                      <AppRoutes />
                    </ErrorBoundary>
                  </DirtyStateProvider>
                </UserRegionProvider>
              </LanguageProvider>
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
